import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { Form, Button, Modal, Toast, ProgressBar, Container } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useGroceryListIngredientStatus from '../hooks/groceryListIngredient';
import { useAddPantryIngredients, useConfirmHasLessThanEnough } from '../hooks/pantryIngredient';
import useGroceryListDifferences from '../hooks/getGroceryListDifferences';
import { useAuth } from '../context/AuthContext';
import AddPantryIngredientButton from '../components/AddPantryIngredientButton';
import '../index.css';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';

const IngredientName = memo(({ children, textKey, onClick, disabled, ...props }) => {
  const spanRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [hasEverOverflowed, setHasEverOverflowed] = useState(false);

  useEffect(() => {
    setHasEverOverflowed(false); // Reset on text change
  }, [textKey]);

  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    const checkOverflow = () => {
      // Add a 2px buffer to prevent oscillation
      const currentlyOverflowing = el.scrollWidth > el.clientWidth + 2;
      setIsOverflowing(prev => {
        if (prev !== currentlyOverflowing) {
          return currentlyOverflowing;
        }
        return prev;
      });
      if (currentlyOverflowing) {
        setHasEverOverflowed(true);
      }
    };

    checkOverflow(); // Initial check

    // Set up ResizeObserver
    const resizeObserver = new window.ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    // Also listen for window resize (in case parent containers change)
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [children]);

  const handleClick = (e) => {
    if (!disabled && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <span
      ref={spanRef}
      className="ms-2 text-truncate"
      onClick={handleClick}
      style={{
        textDecoration: props.lineThrough ? 'line-through' : 'none',
        maxWidth: '240px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontSize: hasEverOverflowed ? '0.75em' : '1em', // Shrink only, reset on text change
        transition: 'font-size 0.2s',
        cursor: disabled ? 'default' : 'pointer',
        ...props.style,
      }}
    >
      {children}
    </span>
  );
});

const MyGroceryList = ({ groceryListId, onItemsAddedToPantry }) => {
  const [searchParams] = useSearchParams();
  
  // Raw data from API
  const [rawIngredients, setRawIngredients] = useState([]);
  // Stable ordered view that preserves positions during interactions
  const [stableIngredients, setStableIngredients] = useState([]);
  const [groceryListDetails, setGroceryListDetails] = useState(null);
  const { 
    setGroceryListIngredientIsInCartStatus, 
    setMultipleGroceryListIngredientsPurchasedStatus,
    updateGroceryListIngredientAdjustedQuantity 
  } = useGroceryListIngredientStatus();
  const { addPantryIngredients, isAdding: isAddingBatch } = useAddPantryIngredients();
  const { differences: pantryItems, isLoading: isLoadingPantryItems, getDifferences } = useGroceryListDifferences();
  const { confirmHasLessThanEnough, isUpdating: isAddingBack } = useConfirmHasLessThanEnough();
  const { pgUser, pgUserId, sessionToken } = useAuth();
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const loadedGroceryListId = useRef(null);
  const [errorAddingToPantry, setErrorAddingToPantry] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showToast, setShowToast] = useState(false); //success toast
  const [toastMessage, setToastMessage] = useState('Items Added to Pantry');
  const [toastStyleError, setToastStyleError] = useState(false);
  const isAddingToPantry = isAddingBatch;
  const [addingBackItemId, setAddingBackItemId] = useState(null);
  const [instacartLoading, setInstacartLoading] = useState(false);
  
  // Track adjusted quantities for each item while shopping
  const [adjustedQuantities, setAdjustedQuantities] = useState({});
  
  // Week toggler state: 'this' or 'next' - initialize from URL parameter if present
  const weekParam = searchParams.get('week');
  const listIdFromUrl = searchParams.get('groceryListId');
  const initialWeek = (weekParam === 'this' || weekParam === 'next') ? weekParam : 'this';
  const [selectedWeek, setSelectedWeek] = useState(initialWeek);
  const [isLoadingWeek, setIsLoadingWeek] = useState(false);
  
  // Sticky progress bar state
  const progressBarRef = useRef(null);
  const [isProgressBarInView, setIsProgressBarInView] = useState(true);
  const [isMobileScreen, setIsMobileScreen] = useState(false);

  // Helper to get date string for a given offset from today
  const getDateWithOffset = useCallback((daysOffset = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + daysOffset);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }, []);

  // Determine which week a grocery list belongs to based on its dates
  const determineWeekFromDates = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return 'this';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    
    // If today falls within the date range, it's "this week"
    if (today >= start && today <= end) {
      return 'this';
    }
    
    // If the start date is in the future, it's "next week"
    if (start > today) {
      return 'next';
    }
    
    // Default to "this" if dates are in the past
    return 'this';
  }, []);

  // Fetch grocery list by date
  const fetchGroceryListByDate = useCallback(async (date) => {
    if (!pgUser?.id || !sessionToken) return null;

    try {
      const response = await fetchWithCSRF(`/api/v1/grocery-list/date/${date}/app_user/${pgUser.id}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch grocery list by date');
      }

      const data = await response.json();
      if (data && data.length > 0 && data[0].data) {
        console.log('🔍 [MyGroceryListAdjustable.js] Grocery list details:', JSON.stringify(data[0].data, null, 2));
        return data[0].data;
      }
      return null;
    } catch (err) {
      console.error('Error fetching grocery list by date:', err);
      return null;
    }
  }, [pgUser?.id, sessionToken]);

  // Fetch current grocery list (when groceryListId is not provided)
  const fetchCurrentGroceryList = useCallback(async () => {
    if (!pgUser?.id || !sessionToken) return null;

    try {
      // Get today's date in YYYY-MM-DD format
      const today = getDateWithOffset(0);
      
      const response = await fetchWithCSRF(`/api/v1/grocery-list/current/${pgUser.id}/date/${today}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch current grocery list');
      }

      const data = await response.json();
      // The response is an array with one item containing the data object
      if (data && data.length > 0 && data[0].data) {
        console.log('🔍 [MyGroceryListAdjustable.js] Current grocery list details:', JSON.stringify(data[0].data, null, 2));
        return data[0].data.grocery_list?.id;
      }
      return null;
    } catch (err) {
      console.error('Error fetching current grocery list:', err);
      return null;
    }
  }, [pgUser?.id, sessionToken, getDateWithOffset]);

  // Helper function to format date range (e.g., "Nov 9 - Nov 15")
  const formatDateRange = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return '';
    
    const formatDate = (dateStr) => {
      const date = new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }, []);

  // Helper function to format quantity as clean fractions when possible
  const formatQuantity = useCallback((num) => {
    const allowedFractions = [
      { fraction: 1/16, label: "1/16", vulgar: "¹⁄₁₆" },
      { fraction: 1/8, label: "1/8", vulgar: "⅛" },
      { fraction: 1/6, label: "1/6", vulgar: "⅙" },
      { fraction: 1/4, label: "1/4", vulgar: "¼" },
      { fraction: 1/3, label: "1/3", vulgar: "⅓" },
      { fraction: 1/2, label: "1/2", vulgar: "½" },
      { fraction: 5/8, label: "5/8", vulgar: "⅝" },
      { fraction: 2/3, label: "2/3", vulgar: "⅔" },
      { fraction: 3/4, label: "3/4", vulgar: "¾" },
      { fraction: 5/6, label: "5/6", vulgar: "⅚" },
    ];

    const parsed = parseFloat(num);
    if (isNaN(parsed)) return num;

    const fractionTolerance = 0.008;
    const wholeTolerance = 0.02;
    const whole = Math.floor(parsed);
    const decimal = parsed - whole;

    // Check if it's basically a whole number
    if (decimal < wholeTolerance || decimal > (1 - wholeTolerance)) {
      return `${decimal > (1 - wholeTolerance) ? whole + 1 : whole}`;
    }

    // Check for fraction match
    const match = allowedFractions.find(f => Math.abs(decimal - f.fraction) < fractionTolerance);

    if (match) {
      const fractionSpan = <span style={{ fontSize: '1.3em' }}>{match.vulgar}</span>;
      if (whole > 0) {
        return <><span>{whole}</span>{fractionSpan}</>;
      }
      return fractionSpan;
    }

    // Fall back to decimal, cleaned up
    const rounded = parsed.toFixed(3);
    // Remove trailing zeros and unnecessary decimal point
    return rounded.replace(/\.?0+$/, '');
  }, []);

  // Initial sorting function
  const sortIngredients = useCallback((ingredients) => {
    // Group by food group first
    const grouped = ingredients.reduce((acc, ingredient) => {
      const group = ingredient.primary_food_group || 'Other';
      if (!acc[group]) acc[group] = [];
      acc[group].push({...ingredient});
      return acc;
    }, {});
    
    // Sort within each group
    Object.keys(grouped).forEach(group => {
      grouped[group].sort((a, b) => {
        // First sort by purchased status (false comes before true)
        if (a.is_purchased !== b.is_purchased) {
          return a.is_purchased ? 1 : -1;
        }
        // Then sort by in-cart status (false comes before true)
        return Number(a.is_in_cart) - Number(b.is_in_cart);
      });
    });
    
    // Flatten grouped and sorted ingredients
    const sortedGroups = Object.keys(grouped).sort();
    const sortedIngredients = [];
    sortedGroups.forEach(group => {
      // Add group marker
      sortedIngredients.push({ isGroupHeader: true, groupName: group });
      // Add ingredients in this group
      sortedIngredients.push(...grouped[group]);
    });
    
    return sortedIngredients;
  }, []);

  const fetchIngredients = useCallback(async (listId) => {
    const idToUse = listId || groceryListId;
    if (!idToUse) return;
    
    try {
      if (!sessionToken) {
        throw new Error('No valid session found');
      }
      const response = await fetchWithCSRF(`/api/v1/grocery-list/${idToUse}/app_user/${pgUser.id}`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      const responseData = await response.json();
      
      // Extract the grocery list details and ingredients from the nested structure
      const groceryList = responseData[0]?.data?.grocery_list;
      const groceryListIngredients = responseData[0]?.data?.grocery_list_ingredients;
      setGroceryListDetails(groceryList);
      
      // Determine and set which week this grocery list belongs to
      let weekForUrl = selectedWeek;
      if (groceryList?.start_date && groceryList?.end_date) {
        const week = determineWeekFromDates(groceryList.start_date, groceryList.end_date);
        weekForUrl = week;
        setSelectedWeek(week);
      }
      
      const mappedIngredients = groceryListIngredients?.map(ingredient => ({
        ...ingredient,
        is_in_cart: ingredient.is_in_cart || false
      })) || [];
      
      // Always update the loaded id reference when fetch completes successfully
      loadedGroceryListId.current = idToUse;
      
      // Set raw ingredients data
      setRawIngredients(mappedIngredients);
      
      // Sort and create stable view of ingredients
      setStableIngredients(sortIngredients(mappedIngredients));

      // Strip groceryListId from the URL after a successful Instacart linkback load so week
      // toggling uses date-based fetches again (avoids being stuck on one list id in the query).
      const glParam = searchParams.get('groceryListId');
      if (glParam != null && String(glParam) === String(idToUse)) {
        navigate(`/grocery-list?week=${encodeURIComponent(weekForUrl)}`, { replace: true });
      }
    } catch (error) {
      console.error('Error fetching grocery list ingredients:', error);
    }
  }, [pgUser?.id, groceryListId, sessionToken, sortIngredients, determineWeekFromDates, searchParams, navigate, selectedWeek]);

  useEffect(() => {
    const loadGroceryList = async () => {
      // Embedded / parent-provided list id takes precedence
      if (groceryListId && loadedGroceryListId.current !== groceryListId) {
        fetchIngredients(groceryListId);
        return;
      }
      // Deep link from Instacart partner_linkback_url: /grocery-list?groceryListId=...
      if (!groceryListId && listIdFromUrl && loadedGroceryListId.current !== listIdFromUrl) {
        fetchIngredients(listIdFromUrl);
        return;
      }
      // If no specific list, fetch the current one based on selected week
      if (!groceryListId && !listIdFromUrl && pgUser?.id && sessionToken) {
        const daysOffset = selectedWeek === 'next' ? 7 : 0;
        const targetDate = getDateWithOffset(daysOffset);
        
        const data = await fetchGroceryListByDate(targetDate);
        
        if (data && data.grocery_list) {
          const groceryList = data.grocery_list;
          const groceryListIngredients = data.grocery_list_ingredients;

          setGroceryListDetails(groceryList);
          loadedGroceryListId.current = groceryList.id;
          
          const mappedIngredients = groceryListIngredients?.map(ingredient => ({
            ...ingredient,
            is_in_cart: ingredient.is_in_cart || false
          })) || [];
          
          setRawIngredients(mappedIngredients);
          setStableIngredients(sortIngredients(mappedIngredients));
        } else {
          // No grocery list found for this week
          setGroceryListDetails(null);
          loadedGroceryListId.current = null;
          setRawIngredients([]);
          setStableIngredients([]);
        }
      }
    };
    
    loadGroceryList();
  }, [groceryListId, listIdFromUrl, pgUser?.id, sessionToken, selectedWeek, getDateWithOffset, fetchGroceryListByDate, sortIngredients, fetchIngredients]);

  // Initialize adjusted quantities when raw ingredients change
  useEffect(() => {
    const initialQuantities = {};
    rawIngredients.forEach(item => {
      // Use adjusted_quantity if it exists, otherwise fall back to original quantity
      initialQuantities[item.id] = item.adjusted_quantity ?? item.quantity;
    });
    setAdjustedQuantities(initialQuantities);
  }, [rawIngredients]);

  // Fetch items covered by pantry when grocery list details are available
  useEffect(() => {
    if (groceryListDetails?.id && groceryListDetails?.meal_plan_id && pgUser?.id && sessionToken) {
      getDifferences(groceryListDetails.id, pgUser.id, groceryListDetails.meal_plan_id);
    }
  }, [groceryListDetails?.id, groceryListDetails?.meal_plan_id, pgUser?.id, sessionToken, getDifferences]);

  // Check if we're on a mobile screen
  useEffect(() => {
    const checkMobileScreen = () => {
      setIsMobileScreen(window.innerWidth <= 991.98);
    };
    
    checkMobileScreen();
    window.addEventListener('resize', checkMobileScreen);
    
    return () => window.removeEventListener('resize', checkMobileScreen);
  }, []);

  // Set up IntersectionObserver for sticky progress bar
  useEffect(() => {
    const element = progressBarRef.current;
    if (!isMobileScreen || !element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsProgressBarInView(entry.isIntersecting);
      },
      {
        threshold: 0,
        // Keep sticky bar visible until natural bar is well into view
        // Negative bottom margin means "consider it in view earlier"
        rootMargin: '0px 0px -100px 0px'
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [isMobileScreen, rawIngredients.length]); // Re-run when list changes

  const handleIsInCart = useCallback(async (ingredientId, isInCart) => {
    // Optimistically update UI first for immediate feedback
    setRawIngredients(prev => prev.map(ingredient => 
      ingredient.id === ingredientId
      ? { ...ingredient, is_in_cart: isInCart }
      : ingredient
    ));
    
    setStableIngredients(prev => prev.map(item => 
      item.id === ingredientId
      ? { ...item, is_in_cart: isInCart }
      : item
    ));
    
    // Then make the API request
    try {
      await setGroceryListIngredientIsInCartStatus(ingredientId, isInCart, pgUser.id);
    } catch (error) {
      console.error('Error updating ingredient cart status:', error);
    }
  }, [setGroceryListIngredientIsInCartStatus, pgUser?.id]);

  const handleDoneShopping = useCallback(async () => {
    const checkedItems = rawIngredients.filter(ingredient => 
      ingredient.is_in_cart && !ingredient.is_purchased
    );
    if (checkedItems.length === 0) return;
    
    // Immediately add items to pantry without confirmation
    try {
      // Create array of pantry ingredients to add in batch
      const today = new Date();
      const purchaseDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      const quantities = Object.fromEntries(checkedItems.map(item => [item.id, adjustedQuantities[item.id] || item.quantity]));
      
      const pantryIngredients = checkedItems.map(item => ({
        app_user_id: pgUser.id,
        pantry_id: pgUser.pantry_id,
        ingredient_id: item.ingredient_id,
        unit_of_measurement: item.unit_of_measurement,
        quantity: quantities[item.id],
        purchase_date: purchaseDate
      }));

      // Add all items to pantry in a single API call
      // Pass true for isConfirmed since user is explicitly adding these items
      const result = await addPantryIngredients(pantryIngredients, true);
      
      if (result) {
        try {
          // Get array of IDs to mark as purchased
          const itemIds = checkedItems.map(item => item.id);
          
          // Mark all items as purchased in a single API call
          await setMultipleGroceryListIngredientsPurchasedStatus(itemIds, true, pgUser.id);
          
          // Update raw data
          setRawIngredients(prev => prev.map(ingredient => 
            itemIds.includes(ingredient.id) 
              ? { ...ingredient, is_purchased: true }
              : ingredient
          ));
          
          // Update stable view WITHOUT reordering
          setStableIngredients(prev => prev.map(item => 
            !item.isGroupHeader && itemIds.includes(item.id) 
              ? { ...item, is_purchased: true }
              : item
          ));

          // Store the items that were added for the success modal
          setPendingItems(checkedItems);

          // Show success modal
          setShowConfirmModal(true);

          // Notify parent component to refresh pantry data
          if (onItemsAddedToPantry) {
            onItemsAddedToPantry();
          }
        } catch (error) {
          console.error('Error marking items as purchased:', error);
          setErrorAddingToPantry(true);
        }
      }
    } catch (error) {
      console.error('Error processing batch add to pantry:', error);
      setErrorAddingToPantry(true);
    }
  }, [rawIngredients, adjustedQuantities, addPantryIngredients, pgUser, setMultipleGroceryListIngredientsPurchasedStatus, onItemsAddedToPantry]);

  const handleCheckoutWithInstacart = useCallback(async () => {
    const listId = groceryListDetails?.id ?? loadedGroceryListId.current;
    const appUserSegment = pgUserId ?? pgUser?.id;
    if (!listId || !appUserSegment || !sessionToken) return;

    setInstacartLoading(true);
    try {
      const response = await fetchWithCSRF(
        `/api/v1/grocery-list/${listId}/instacart-link/app_user/${appUserSegment}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );
      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      if (!response.ok) {
        throw new Error(data.error || `Could not create Instacart list (${response.status})`);
      }
      if (data.products_link_url) {
        window.open(data.products_link_url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('No checkout link returned');
      }
    } catch (err) {
      console.error('Instacart checkout:', err);
      setToastStyleError(true);
      setToastMessage(err.message || 'Could not open Instacart');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    } finally {
      setInstacartLoading(false);
    }
  }, [groceryListDetails?.id, pgUser?.id, pgUserId, sessionToken]);

  const handleNavigateToMealPlan = useCallback(() => {
    setShowConfirmModal(false);
    navigate('/meal-plan');
  }, [navigate]);

  // Handle week toggle
  const handleWeekToggle = useCallback(async (week) => {
    if (week === selectedWeek || isLoadingWeek) return;
    
    setIsLoadingWeek(true);
    setSelectedWeek(week);
    
    try {
      // Calculate the date based on selected week
      const daysOffset = week === 'next' ? 7 : 0;
      const targetDate = getDateWithOffset(daysOffset);
      
      // Fetch grocery list for that date
      const data = await fetchGroceryListByDate(targetDate);
      
      if (data && data.grocery_list) {
        const groceryList = data.grocery_list;
        const groceryListIngredients = data.grocery_list_ingredients;

        console.log('🔍 [MyGroceryList.js] Grocery list details:', groceryList);
        console.log('🔍 [MyGroceryList.js] Grocery list ingredients:', groceryListIngredients);
        setGroceryListDetails(groceryList);
        loadedGroceryListId.current = groceryList.id;
        
        const mappedIngredients = groceryListIngredients?.map(ingredient => ({
          ...ingredient,
          is_in_cart: ingredient.is_in_cart || false
        })) || [];
        
        setRawIngredients(mappedIngredients);
        setStableIngredients(sortIngredients(mappedIngredients));
      } else {
        // No grocery list found for this week
        setGroceryListDetails(null);
        loadedGroceryListId.current = null;
        setRawIngredients([]);
        setStableIngredients([]);
      }
    } catch (error) {
      console.error('Error fetching grocery list for week:', error);
    } finally {
      setIsLoadingWeek(false);
    }
  }, [selectedWeek, isLoadingWeek, getDateWithOffset, fetchGroceryListByDate, sortIngredients]);

  // Handle adding an item back to the grocery list (user says they don't have enough)
  const handleAddBackToGroceryList = useCallback(async (item) => {
    if (!pgUser?.id || !pgUser?.pantry_id || addingBackItemId) return;
    
    setAddingBackItemId(item.ingredient_id);
    
    try {
      // Call confirmHasLessThanEnough with quantity 0 to indicate user doesn't have this
      await confirmHasLessThanEnough(
        item.ingredient_id,
        pgUser.pantry_id,
        pgUser.id,
        0,
        item.unit_of_measurement
      );
      
      // Refresh the grocery list to show the newly added item
      if (loadedGroceryListId.current) {
        await fetchIngredients(loadedGroceryListId.current);
      }
      
      // Refresh the differences to remove this item from "You already have"
      if (groceryListDetails?.id && groceryListDetails?.meal_plan_id) {
        await getDifferences(groceryListDetails.id, pgUser.id, groceryListDetails.meal_plan_id);
      }
      
      setToastStyleError(false);
      setToastMessage('Item added to grocery list');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error('Error adding item back to grocery list:', error);
      setShowErrorToast(true);
      setTimeout(() => setShowErrorToast(false), 3000);
    } finally {
      setAddingBackItemId(null);
    }
  }, [pgUser?.id, pgUser?.pantry_id, addingBackItemId, confirmHasLessThanEnough, fetchIngredients, getDifferences, groceryListDetails]);

  const roundToNearestQuarter = useCallback((value) => Math.round(value * 4) / 4, []);

  // Handle quantity adjustments for individual items in the main list
  const handleItemQuantityChange = useCallback(async (id, delta) => {
    // Find the original quantity for this item
    const item = rawIngredients.find(i => i.id === id);
    if (!item) return;
    
    const originalQuantity = item.quantity;
    
    // Update local state first for immediate UI feedback
    const newQty = await new Promise(resolve => {
      setAdjustedQuantities(prev => {
        const raw = (parseFloat(prev[id]) || originalQuantity) + delta;
        // Don't allow quantity to go below the original required quantity
        const calculatedQty = Math.max(originalQuantity, roundToNearestQuarter(raw));
        const finalQty = parseFloat(calculatedQty.toFixed(3));
        resolve(finalQty);
        return { ...prev, [id]: finalQty };
      });
    });
    
    // Update the ingredient objects to persist adjusted_quantity
    setRawIngredients(prev => prev.map(ingredient => 
      ingredient.id === id
      ? { ...ingredient, adjusted_quantity: newQty }
      : ingredient
    ));
    
    setStableIngredients(prev => prev.map(item => 
      item.isGroupHeader ? item : (
        item.id === id
        ? { ...item, adjusted_quantity: newQty }
        : item
      )
    ));
    
    // Update the database with the new adjusted quantity
    try {
      await updateGroceryListIngredientAdjustedQuantity(id, newQty, pgUser.id);
    } catch (error) {
      console.error('Error updating adjusted quantity:', error);
      // Optionally show an error toast here
    }
  }, [rawIngredients, roundToNearestQuarter, updateGroceryListIngredientAdjustedQuantity, pgUser?.id]);


  // Calculate progress stats
  const progressStats = useMemo(() => {
    const totalItems = rawIngredients.length;
    const checkedItems = rawIngredients.filter(item => item.is_in_cart || item.is_purchased).length;
    const percentage = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;
    
    return { totalItems, checkedItems, percentage };
  }, [rawIngredients]);

  // Combine purchased items with pantry items for display
  const combinedPantryItems = useMemo(() => {
    const purchasedItems = rawIngredients
      .filter(item => item.is_purchased)
      .map(item => ({
        ingredient_id: item.ingredient_id,
        ingredient_name: item.ingredient_name,
        unit_of_measurement: item.unit_of_measurement,
        pantry_quantity: item.quantity,
        isPurchased: true,
        groceryListItemId: item.id
      }));
    
    const existingPantryItems = (pantryItems || []).map(item => ({
      ...item,
      isPurchased: false
    }));
    
    // Purchased items first, then existing pantry items
    return [...purchasedItems, ...existingPantryItems];
  }, [rawIngredients, pantryItems]);

  // Memoize Modal component - now a success message
  const ConfirmModal = useMemo(() => (
    <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
      <Modal.Body className="text-center py-5">
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1.5rem' }}>Woohoo! 🎉</h2>
        <p style={{ fontSize: '1.1rem', marginBottom: '2rem', color: '#495057' }}>
          We've added the items you bought to your virtual pantry
        </p>
        <Button 
          variant="primary" 
          onClick={handleNavigateToMealPlan}
          style={{ 
            backgroundColor: '#52cd92', 
            borderColor: '#52cd92',
            padding: '12px 32px',
            fontSize: '1.1rem',
            fontWeight: '500'
          }}
        >
          Let's Start Cooking
        </Button>
      </Modal.Body>
    </Modal>
  ), [showConfirmModal, handleNavigateToMealPlan]);

  return (
    <Container fluid className="" style={{ maxWidth: '800px', paddingBottom: '1rem' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
        {/* Header */}
        <div style={{
          padding: '0.5rem 1.5rem',
        }}>
          <h2 className="mb-1" style={{ textAlign: 'left' }}>{selectedWeek === 'this' ? "This Week's Grocery List" : "Next Week's Grocery List"}</h2>
          
          {/* Week Toggler */}
          <div className="d-flex align-items-center justify-content-start" style={{ position: 'relative' }}>
            <div 
              style={{
                display: 'flex',
                backgroundColor: 'transparent',
                borderRadius: '8px',
                padding: '4px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
                position: 'relative'
              }}
            >
              {/* Sliding background */}
              <div
                style={{
                  position: 'absolute',
                  top: '4px',
                  left: selectedWeek === 'this' ? '4px' : 'calc(50%)',
                  width: 'calc(50% - 4px)',
                  height: 'calc(100% - 8px)',
                  backgroundColor: '#52cd92',
                  borderRadius: '6px',
                  transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 0
                }}
              />
              <button
                onClick={() => handleWeekToggle('this')}
                disabled={isLoadingWeek}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  color: selectedWeek === 'this' ? 'white' : '#666',
                  fontWeight: selectedWeek === 'this' ? '600' : '400',
                  cursor: isLoadingWeek ? 'not-allowed' : 'pointer',
                  transition: 'color 0.2s ease, font-weight 0.2s ease',
                  fontSize: '0.9rem',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                This Week
              </button>
              <button
                onClick={() => handleWeekToggle('next')}
                disabled={isLoadingWeek}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  color: selectedWeek === 'next' ? 'white' : '#666',
                  fontWeight: selectedWeek === 'next' ? '600' : '400',
                  cursor: isLoadingWeek ? 'not-allowed' : 'pointer',
                  transition: 'color 0.2s ease, font-weight 0.2s ease',
                  fontSize: '0.9rem',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                Next Week
              </button>
            </div>
            
            {/* Date Range - positioned to the right */}
            <h6 
              style={{ 
                fontWeight: '300', 
                fontStyle: 'italic',
                position: 'absolute',
                right: 0,
                margin: 0,
                fontSize: '0.85rem',
                color: '#666'
              }}
            >
              {formatDateRange(groceryListDetails?.start_date, groceryListDetails?.end_date)}
            </h6>
          </div>

          {errorAddingToPantry && (
            <div className="alert alert-danger mt-2 px-2 py-2" role="alert">
              Error adding items to pantry. Please try again.
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '1rem' }}>
          <ul className="list-unstyled">
            {(() => {
              // Show loading state if we haven't loaded a grocery list yet
              if (stableIngredients.length === 0) {
                if (isLoadingWeek) {
                  return (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status" style={{ color: '#52cd92' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading grocery list...</p>
                    </div>
                  );
                } else if (!loadedGroceryListId.current && !groceryListDetails) {
                  return (
                    <div className="text-center py-4">
                      <p className="mt-2" style={{ color: '#666' }}>
                        No grocery list for {selectedWeek === 'this' ? 'this week' : 'next week'}.
                      </p>
                      <small className="text-muted">
                        Create a meal plan to generate a grocery list.
                      </small>
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center py-4">
                      <p className="mt-2">No items on your list!</p>
                    </div>
                  );
                }
              }
              
              return stableIngredients.map((item, index) => {
                if (item.isGroupHeader) {
                  // Render group header
                  return (
                    <li key={`group-${item.groupName}`}>
                      <h6 style={{ 
                        marginTop: '1rem', 
                        marginBottom: '0.5rem', 
                        color: 'black', 
                        fontWeight: 700,
                        fontSize: '1rem',
                        textAlign: 'left',
                        paddingLeft: '10px'
                      }}>
                        {item.groupName}
                      </h6>
                    </li>
                  );
                } else {
                  // Render ingredient item
                  return (
                    <li 
                      key={item.id} 
                      className={`d-flex align-items-center py-2 px-1 border-bottom ${item.is_purchased ? 'text-muted' : ''}`}
                      style={{ 
                        opacity: item.is_purchased ? '0.6' : '1'
                      }}
                    >
                      <div style={{ width: '15%', textAlign: 'center' }}>
                        <Form.Check
                          type="checkbox"
                          checked={item.is_purchased || item.is_in_cart}
                          onChange={(e) => handleIsInCart(item.id, e.target.checked)}
                          className="custom-checkbox"
                          disabled={item.is_purchased}
                          style={{ 
                            cursor: item.is_purchased ? 'not-allowed' : 'pointer'
                          }}
                        />
                      </div>

                      <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                        <img
                          src={getIngredientImageUrl(item.ingredient_id)}
                          alt={item.ingredient_name}
                          style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '4px', marginRight: '0.5rem', background: '#ffffff' }}
                          onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMTIgMTZIMTZWMTlIMjFDMjIuMTA0IDE5IDIyLjg5NiAxOC4yMDggMjIuODk2IDE3VjE1QzIyLjg5NiAxMy43OTIgMjIuMTA0IDEzIDIxIDEzSDE2VjE2SDEyWiIgZmlsbD0iIzkwOTA5MCIvPjwvc3ZnPg=='; }}
                        />
                        <span
                          onClick={() => {
                            if (!item.is_purchased) {
                              const newIsInCartValue = !(item.is_purchased || item.is_in_cart);
                              handleIsInCart(item.id, newIsInCartValue);
                            }
                          }}
                          style={{
                            textDecoration: (item.is_purchased || item.is_in_cart) ? 'line-through' : 'none',
                            fontSize: '0.85rem',
                            cursor: item.is_purchased ? 'default' : 'pointer',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.ingredient_name}
                          {item.notes && <small className="text-muted ms-1">({item.notes})</small>}
                        </span>
                      </div>

                      <div 
                        style={{ 
                          minWidth: '140px',
                          maxWidth: '140px',
                          textAlign: 'right',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: '0.1rem',
                          minHeight: '40px'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: '0.25rem'
                        }}>
                          {!item.is_purchased && (adjustedQuantities[item.id] ?? item.quantity) > item.quantity && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemQuantityChange(item.id, -0.25);
                              }}
                              style={{
                                width: '24px',
                                height: '24px',
                                padding: 0,
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                color: '#333',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              −
                            </button>
                          )}
                          <span style={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            minWidth: '60px',
                            textAlign: 'center',
                            color: (adjustedQuantities[item.id] ?? item.quantity) > item.quantity ? '#243029' : 'inherit',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '24px',
                            lineHeight: '24px',
                            gap: '0.3rem'
                          }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                              {formatQuantity(adjustedQuantities[item.id] ?? item.quantity)}
                            </span>
                            <span>{item.unit_of_measurement}</span>
                          </span>
                          {!item.is_purchased && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemQuantityChange(item.id, 0.25);
                              }}
                              style={{
                                width: '24px',
                                height: '24px',
                                padding: 0,
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                color: '#333',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              +
                            </button>
                          )}
                        </div>
                        {(adjustedQuantities[item.id] ?? item.quantity) > item.quantity && (
                          <small style={{ 
                            fontSize: '0.7rem', 
                            color: '#999',
                            textDecoration: 'line-through',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            height: '16px',
                            lineHeight: '16px',
                            gap: '0.3rem'
                          }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                              {formatQuantity(item.quantity)}
                            </span>
                            <span>{item.unit_of_measurement}</span>
                          </small>
                        )}
                      </div>
                    </li>
                  );
                }
              });
            })()}
          </ul>

          {/* Progress Bar */}
          {progressStats.totalItems > 0 && (
            <div ref={progressBarRef} className="mb-3 mt-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small style={{ fontWeight: '600', color: '#333' }}>
                  {progressStats.checkedItems} of {progressStats.totalItems} items in cart
                </small>
                <small style={{ fontWeight: '600', color: '#52cd92' }}>
                  {progressStats.percentage}%
                </small>
              </div>
              <ProgressBar 
                now={progressStats.percentage} 
                style={{ height: '8px' }}
                variant="success"
              />
            </div>
          )}

          {/* Estimated Cost */}
          {groceryListDetails && (
            <div className="small text-muted mb-3" style={{ textAlign: 'left' }}>
              Estimated Cost: ${(groceryListDetails.estimated_total_cost_in_cents / 100).toFixed(2)}
            </div>
          )}

          {/* Complete Shopping + Instacart (side by side) */}
          <div className="d-flex flex-row gap-2 align-items-stretch">
            <Button 
              onClick={handleDoneShopping}
              disabled={isAddingToPantry || !rawIngredients.some(i => i.is_in_cart && !i.is_purchased)}
              className="flex-fill"
              style={{
                backgroundColor: '#52cd92',
                borderColor: '#52cd92',
                fontWeight: '500',
                fontSize: 'clamp(0.8rem, 2.5vw, 1rem)',
              }}
            >
              Complete Shopping
            </Button>
            <Button
              variant="outline-success"
              onClick={handleCheckoutWithInstacart}
              disabled={
                instacartLoading
                || !(groceryListDetails?.id ?? loadedGroceryListId.current)
                || !rawIngredients.some(i => !i.is_purchased)
              }
              className="flex-fill"
              style={{
                borderColor: '#52cd92',
                color: '#243029',
                fontWeight: '500',
                fontSize: 'clamp(0.8rem, 2.5vw, 1rem)',
              }}
            >
              {instacartLoading ? 'Opening…' : 'Order online'}
            </Button>
          </div>
        </div>
      </div>

      {/* You Already Have Section - Separate Container */}
      {combinedPantryItems && combinedPantryItems.length > 0 && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', marginTop: '1.5rem' }}>
          <div style={{ padding: '1rem' }}>
            <h4 style={{ 
              fontWeight: '600', 
              color: '#495057', 
              marginBottom: '0 !important',
              textAlign: 'left',
              // fontSize: '0.95rem'
            }}>
              Items You Already Have For {selectedWeek === 'this' ? "This Week" : "Next Week"}
            </h4>
            {/* <div style={{textAlign: 'left', fontSize: '0.85rem', color: '#666'}}>No need to buy these</div> */}
            <ul className="list-unstyled" style={{ paddingLeft: '0' }}>
              {combinedPantryItems.map((item, index) => (
                <li 
                  key={item.isPurchased ? `purchased-${item.groceryListItemId}` : `pantry-${item.ingredient_id}`}
                  className="d-flex align-items-center py-2 px-1 border-bottom"
                  style={{ 
                    opacity: 1.0
                  }}
                >
                  <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                    <img
                      src={getIngredientImageUrl(item.ingredient_id)}
                      alt={item.ingredient_name}
                      style={{ 
                        width: '28px', 
                        height: '28px', 
                        objectFit: 'cover', 
                        borderRadius: '4px', 
                        marginRight: '0.5rem',
                        background: '#ffffff'
                      }}
                      onError={e => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMTIgMTZIMTZWMTlIMjFDMjIuMTA0IDE5IDIyLjg5NiAxOC4yMDggMjIuODk2IDE3VjE1QzIyLjg5NiAxMy43OTIgMjIuMTA0IDEzIDIxIDEzSDE2VjE2SDEyWiIgZmlsbD0iIzkwOTA5MCIvPjwvc3ZnPg=='; }}
                    />
                    <span
                      style={{
                        fontSize: '0.85rem',
                        color: '#495057',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.ingredient_name}
                    </span>
                  </div>
                  
                  {/* Quantity column */}
                  <div 
                    style={{ 
                      minWidth: '80px', 
                      textAlign: 'right', 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      marginRight: '0.5rem'
                    }}
                  >
                    {formatQuantity(item.pantry_quantity)} {item.unit_of_measurement}
                  </div>
                  
                  {/* Add back button column */}
                  <div style={{ flexShrink: 0 }}>
                    <button
                      onClick={async () => {
                        if (item.isPurchased) {
                          // For purchased items, unmark as purchased and uncheck from cart
                          try {
                            await setMultipleGroceryListIngredientsPurchasedStatus([item.groceryListItemId], false, pgUser.id);
                            
                            // Update UI optimistically
                            setRawIngredients(prev => prev.map(ingredient => 
                              ingredient.id === item.groceryListItemId
                                ? { ...ingredient, is_purchased: false, is_in_cart: false }
                                : ingredient
                            ));
                            
                            setStableIngredients(prev => prev.map(stableItem => 
                              !stableItem.isGroupHeader && stableItem.id === item.groceryListItemId
                                ? { ...stableItem, is_purchased: false, is_in_cart: false }
                                : stableItem
                            ));
                          } catch (error) {
                            console.error('Error unmarking item as purchased:', error);
                          }
                        } else {
                          // For existing pantry items, add back to grocery list
                          handleAddBackToGroceryList(item);
                        }
                      }}
                      disabled={!item.isPurchased && (addingBackItemId === item.ingredient_id || isAddingBack)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '0.75rem',
                        border: '1px solid #52cd92',
                        borderRadius: '4px',
                        backgroundColor: (!item.isPurchased && addingBackItemId === item.ingredient_id) ? '#e9ecef' : 'transparent',
                        color: '#52cd92',
                        cursor: (!item.isPurchased && addingBackItemId === item.ingredient_id) ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                        fontWeight: '500',
                        transition: 'all 0.15s ease',
                        }}
                      onMouseEnter={(e) => {
                        if (item.isPurchased || addingBackItemId !== item.ingredient_id) {
                          e.target.style.backgroundColor = '#52cd92';
                          e.target.style.color = 'white';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (item.isPurchased || addingBackItemId !== item.ingredient_id) {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#52cd92';
                        }
                      }}
                    >
                      {!item.isPurchased && addingBackItemId === item.ingredient_id ? '...' : 'Add back to list'}
                    </button>
                  </div>
                </li>
              ))}
              
              {/* Add Pantry Ingredient Button Row */}
              <li 
                className="d-flex align-items-center py-2 px-1"
                style={{ 
                  // borderTop: '1px solid #dee2e6',
                  marginTop: '0.5rem',
                  paddingTop: '1rem'
                }}
              >
                <div style={{ width: '100%', display: 'flex', justifyContent: 'left' }}>
                  <AddPantryIngredientButton
                    pantryId={pgUser?.pantry_id}
                    appUserId={pgUser?.id}
                    onSubmit={() => {
                      // Refresh both the grocery list and pantry differences after adding an item
                      if (loadedGroceryListId.current) {
                        fetchIngredients(loadedGroceryListId.current);
                      }
                      if (groceryListDetails?.id && groceryListDetails?.meal_plan_id && pgUser?.id) {
                        getDifferences(groceryListDetails.id, pgUser.id, groceryListDetails.meal_plan_id);
                      }
                    }}
                    onGroceryListUpdate={onItemsAddedToPantry}
                    buttonText="+ Add Pantry Item"
                    buttonStyle={{
                      padding: '8px 20px',
                      fontSize: '0.9rem',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#52cd92',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Modals and Toasts */}
      {ConfirmModal}

      {/* Error Toast */}
      <Toast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        style={{
          position: 'fixed',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#f8d7da',
          borderColor: '#f5c2c7',
          color: '#842029',
          zIndex: 2000,
          minWidth: '250px'
        }}
      >
        <Toast.Header>
          <strong className="me-auto">Error</strong>
        </Toast.Header>
        <Toast.Body>
          An error occurred. Please refresh the page and try again.
        </Toast.Body>
      </Toast>

      {/* Success / notice Toast */}
      <Toast 
        show={showToast} 
        onClose={() => {
          setShowToast(false);
          setToastStyleError(false);
        }}
        style={{
          position: 'fixed',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          backgroundColor: toastStyleError ? '#f8d7da' : '#52cd92',
          border: toastStyleError ? '1px solid #f5c2c7' : 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          minWidth: '300px'
        }}
      >
        <Toast.Header style={{ 
          backgroundColor: toastStyleError ? '#f8d7da' : '#52cd92', 
          borderBottom: toastStyleError ? '1px solid #f5c2c7' : '1px solid rgba(255,255,255,0.2)',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          width: '100%'
        }}>
          <strong style={{ color: toastStyleError ? '#842029' : 'white' }}>{toastMessage}</strong>
        </Toast.Header>
      </Toast>

      {/* Sticky Progress Bar for Mobile - shows when natural progress bar is out of view */}
      {isMobileScreen && !isProgressBarInView && progressStats.totalItems > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(60px + env(safe-area-inset-bottom))', // Above bottom nav
            left: 0,
            right: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
            padding: '12px 16px 16px 16px',
            zIndex: 999,
            transition: 'transform 0.3s ease-in-out',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small style={{ fontWeight: '600', color: '#333' }}>
              {progressStats.checkedItems} of {progressStats.totalItems} items in cart
            </small>
            <small style={{ fontWeight: '600', color: '#52cd92' }}>
              {progressStats.percentage}%
            </small>
          </div>
          <ProgressBar 
            now={progressStats.percentage} 
            style={{ height: '8px' }}
            variant="success"
            className="mb-2"
          />
          <div className="d-flex flex-row gap-2 align-items-stretch">
            <Button 
              onClick={handleDoneShopping}
              disabled={isAddingToPantry || !rawIngredients.some(i => i.is_in_cart && !i.is_purchased)}
              className="flex-fill"
              style={{
                backgroundColor: '#52cd92',
                borderColor: '#52cd92',
                fontWeight: '500',
                fontSize: 'clamp(0.75rem, 2.2vw, 0.95rem)',
              }}
            >
              Complete Shopping
            </Button>
            <Button
              variant="outline-success"
              onClick={handleCheckoutWithInstacart}
              disabled={
                instacartLoading
                || !(groceryListDetails?.id ?? loadedGroceryListId.current)
                || !rawIngredients.some(i => !i.is_purchased)
              }
              className="flex-fill"
              style={{
                borderColor: '#52cd92',
                color: '#243029',
                fontWeight: '500',
                fontSize: 'clamp(0.75rem, 2.2vw, 0.95rem)',
              }}
            >
              {instacartLoading ? 'Opening…' : 'Order online'}
            </Button>
          </div>
        </div>
      )}
    </Container>
  );
};

export default MyGroceryList;

